import { useMemo, useState } from 'react';
import { CollectionCard } from '@/components/CollectionCard';
import { ExperienceCard } from '@/components/ExperienceCard';
import { useCollections } from '@/hooks/useCollections';
import { useExperiences } from '@/hooks/useExperiences';
import { cn } from '@/lib/utils';
import { experienceRegionLabels, experienceRegionOrder, type ExperienceRegionType } from '@/types/types_experience';

type RegionFilter = ExperienceRegionType | 'all';

/** Public catalog of experiences and collections, with a region filter. */
export function PageCatalog() {
  const { experiences, loading: loadingExperiences } = useExperiences();
  const { collections, loading: loadingCollections } = useCollections();
  const [region, setRegion] = useState<RegionFilter>('all');
  const loading = loadingExperiences || loadingCollections;

  // Standalone experiences (not part of a collection); experiences that
  // belong to a collection are reachable from that collection's page instead.
  const standalone = useMemo(
    () => experiences.filter((e) => e.collectionId === null && e.status === 'active'),
    [experiences],
  );
  const activeCollections = useMemo(() => collections.filter((c) => c.status === 'active'), [collections]);

  // Ordered by the canonical region order rather than whatever order the
  // documents happen to load in, so the filter row is stable.
  const regionsPresent = useMemo(() => {
    const set = new Set<ExperienceRegionType>();
    standalone.forEach((e) => set.add(e.region));
    activeCollections.forEach((c) => set.add(c.region));
    return experienceRegionOrder.filter((r) => set.has(r));
  }, [standalone, activeCollections]);

  const filteredExperiences = region === 'all' ? standalone : standalone.filter((e) => e.region === region);
  const filteredCollections =
    region === 'all' ? activeCollections : activeCollections.filter((c) => c.region === region);

  return (
    <div className='tw:mx-auto tw:max-w-6xl tw:px-6 tw:py-16'>
      <header className='tw:mb-12 tw:text-center'>
        <p className='tw:text-xs tw:tracking-[0.3em] tw:uppercase tw:text-ed-gold'>The Collection</p>
        <h1 className='tw:mt-3 tw:font-serif tw:text-5xl tw:font-light'>Exclusive Experiences</h1>
        <p className='tw:mx-auto tw:mt-4 tw:max-w-xl tw:text-ed-charcoal/80'>
          A curated portfolio of private, reservation-only experiences across Japan.
        </p>
      </header>

      {/* Region filter */}
      <div className='tw:mb-12 tw:flex tw:flex-wrap tw:justify-center tw:gap-3'>
        <FilterButton active={region === 'all'} onClick={() => setRegion('all')}>
          All Regions
        </FilterButton>
        {regionsPresent.map((r) => (
          <FilterButton key={r} active={region === r} onClick={() => setRegion(r)}>
            {experienceRegionLabels[r]}
          </FilterButton>
        ))}
      </div>

      {loading ? (
        <p className='tw:text-center tw:text-ed-stone'>Loading experiences…</p>
      ) : (
        <div className='tw:grid tw:gap-8 tw:sm:grid-cols-2 tw:lg:grid-cols-3'>
          {filteredCollections.map((collection) => (
            <CollectionCard key={collection.id} collection={collection} />
          ))}
          {filteredExperiences.map((experience) => (
            <ExperienceCard key={experience.id} experience={experience} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'tw:border tw:px-5 tw:py-2 tw:text-xs tw:tracking-[0.15em] tw:uppercase tw:transition-colors',
        active
          ? 'tw:border-ed-gold tw:bg-ed-gold tw:text-ed-black'
          : 'tw:border-ed-black/20 tw:text-ed-charcoal tw:hover:border-ed-gold tw:hover:text-ed-gold',
      )}
    >
      {children}
    </button>
  );
}
